// features/bookmarks/BookmarkCard.tsx — one saved-item card (all bookmark types).

import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { IconButton } from '@/ui';
import { Icon } from '@/icons';
import type { Bookmark, BookmarkFolder } from '@/core/models/bookmark';
import { TYPE_META } from './meta';
import { MoveToFolderMenu } from './MoveToFolderMenu';
import { discussionHref } from '@/core/services/discussionsService';

export interface BookmarkCardProps {
  bookmark: Bookmark;
  folders: BookmarkFolder[];
  onRemove: (id: string) => void;
  onMove: (bookmarkId: string, folderId: string) => void;
}

const Card = styled.article`
  display: flex; align-items: flex-start; gap: 12px;
  padding: 14px; border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;
const IconChip = styled.span`
  flex: none; display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.accentCyan};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;
const Body = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px;`;
const titleCss = `font-size: inherit; font-weight: 700;`;
const TitleLink = styled(Link)`
  ${titleCss}
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;
const TitleText = styled.span`${titleCss} color: ${({ theme }) => theme.color.textPrimary};`;
const Source = styled.div`font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted};`;
const Snippet = styled.div`font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textPrimary};`;
const Meta = styled.div`
  display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
  font-size: ${({ theme }) => theme.fontSize.xs}; color: ${({ theme }) => theme.color.textMuted};
`;
const Tag = styled.span`
  padding: 2px 8px; border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;
const Actions = styled.div`flex: none; display: flex; gap: 4px;`;

export function BookmarkCard({ bookmark: b, folders, onRemove, onMove }: BookmarkCardProps) {
  const meta = TYPE_META[b.type];
  const folderName = folders.find((f) => f.id === b.folderId)?.name ?? b.folderId;
  return (
    <Card>
      <IconChip aria-hidden><Icon name={meta.icon} size={20} /></IconChip>
      <Body>
        {b.postId ? (
          <TitleLink to={discussionHref(b.postId,b.commentId)}>{b.title}</TitleLink>
        ) : (
          <TitleText>{b.title}</TitleText>
        )}
        <Source>{b.source}{b.handle ? ` ${b.handle}` : ''}</Source>
        {b.snippet && <Snippet>{b.snippet}</Snippet>}
        {b.note && <Snippet>Private note: {b.note}</Snippet>}
        <Meta>
          <Tag>{folderName}</Tag>
          <span>{meta.label}</span>
          <span>{b.time}</span>
        </Meta>
      </Body>
      <Actions>
        <MoveToFolderMenu
          currentFolderId={b.folderId}
          folders={folders}
          onMove={(folderId) => onMove(b.id, folderId)}
        />
        <IconButton icon="bookmark" label="Remove bookmark" size={16} onClick={() => onRemove(b.id)} />
      </Actions>
    </Card>
  );
}
